import { api } from '../api';
import type { UserRole, UsuarioListItem } from '../types';
import type { PermisoOverrides } from '../permissions/catalog';
import type { EquipoVenta } from '../catalogs/equipos-venta';

export interface InviteUsuarioPayload {
  email: string;
  nombre?: string;
  rol: UserRole;
  empresaIds?: string[];
}

export async function listUsuarios(filter: {
  rol?: UserRole;
  empresaId?: string;
  search?: string;
}): Promise<UsuarioListItem[]> {
  const { data } = await api.get<UsuarioListItem[]>('/usuarios', { params: filter });
  return data;
}

/**
 * Integración que consume la API con un token m2m. La crea sola el provisioning
 * JIT la primera vez que pide un token, INACTIVA y sin organización: hasta que
 * un SA le da las dos cosas, sus llamadas reciben 401 o 403.
 */
export interface CuentaServicio {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  empresaIds: string[];
  /** Client de Keycloak que presentó el token (`azp`): dice de qué integración vino. */
  azp?: string;
  creadoAt?: string;
  /** Accesos rechazados desde que se creó. Si sube, algo está fallando ahora mismo. */
  rechazos: number;
  ultimoRechazoAt?: string;
  /** Qué le falta para poder operar. Vacío = lista. */
  falta: Array<'habilitar' | 'organizacion'>;
  /** Overrides de permisos. En una integración es TODO lo que puede hacer. */
  overrides?: PermisoOverrides;
}

export async function listCuentasServicio(): Promise<CuentaServicio[]> {
  const { data } = await api.get<CuentaServicio[]>('/usuarios/cuentas-servicio');
  return data;
}

export async function getUsuario(id: string): Promise<UsuarioListItem> {
  const { data } = await api.get<UsuarioListItem>(`/usuarios/${id}`);
  return data;
}

export async function invitarUsuario(payload: InviteUsuarioPayload): Promise<UsuarioListItem> {
  const { data } = await api.post<UsuarioListItem>('/usuarios/invitar', payload);
  return data;
}

export async function reinvitarUsuario(id: string): Promise<UsuarioListItem> {
  const { data } = await api.post<UsuarioListItem>(`/usuarios/${id}/reinvitar`);
  return data;
}

export async function actualizarUsuario(
  id: string,
  patch: {
    nombre?: string;
    empresaIds?: string[];
    activo?: boolean;
    binaryUsername?: string;
    equipo?: EquipoVenta | null;
  },
): Promise<UsuarioListItem> {
  const { data } = await api.put<UsuarioListItem>(`/usuarios/${id}`, patch);
  return data;
}

export async function eliminarUsuario(id: string): Promise<void> {
  await api.delete(`/usuarios/${id}`);
}

/**
 * Asigna una empresa existente a un usuario existente. NO requiere
 * confirmación del usuario destino — el SA/Admin decide y queda hecho.
 * Idempotente.
 */
export async function asignarEmpresaAUsuario(
  usuarioId: string,
  empresaId: string,
): Promise<UsuarioListItem> {
  const { data } = await api.post<UsuarioListItem>(`/usuarios/${usuarioId}/empresas/${empresaId}`);
  return data;
}

/**
 * Quita una empresa de un usuario. Idempotente.
 */
export async function quitarEmpresaDeUsuario(
  usuarioId: string,
  empresaId: string,
): Promise<UsuarioListItem> {
  const { data } = await api.delete<UsuarioListItem>(
    `/usuarios/${usuarioId}/empresas/${empresaId}`,
  );
  return data;
}

/**
 * Setea los overrides de permisos por vista de un usuario (allow/deny).
 * Reemplaza los arrays completos. El permiso efectivo lo recalcula el backend
 * (defaults del rol ⊕ overrides). Devuelve el usuario con `permisos`/`overrides`.
 */
export async function setPermisosUsuario(
  usuarioId: string,
  overrides: { allow: string[]; deny: string[] },
): Promise<UsuarioListItem> {
  const { data } = await api.put<UsuarioListItem>(`/usuarios/${usuarioId}/permisos`, overrides);
  return data;
}
